import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

window.addEventListener("DOMContentLoaded", () => {
	gsap.registerPlugin(ScrollTrigger);

	const debug = new URLSearchParams(window.location.search).get("debug") === "1";

	// ─── DEBUG: MAP THE PAGE STRUCTURE ──────────────────────────────────
	if (debug) {
		const root = document.querySelector(".vp-modules.parallax-article");
		if (root) {
			console.group("[Page Structure] children of .parallax-article");
			Array.from(root.children).forEach((el, idx) => {
				let type = "unknown";
				let label = "";
				if (el.classList.contains("vp-parallax-backgrounds")) {
					type = "BACKGROUNDS";
					label = `(${el.children.length} items)`;
				} else if (el.classList.contains("vp-parallax-section")) {
					if (el.classList.contains("page-title")) {
						type = "PARALLAX (hero)";
					} else if (el.classList.contains("scroll-trigger")) {
						type = "PARALLAX (scroll-trigger)";
					} else {
						type = "PARALLAX (other)";
					}
					const cw = el.querySelector(".content-wrapper");
					if (cw) label = `"${cw.textContent.trim().split(/\s+/).slice(0, 5).join(" ")}…"`;
				} else if (el.classList.contains("vp-parallax-scroll-down")) {
					type = "SCROLL-DOWN";
				} else if (el.classList.contains("vp-content-body-module")) {
					type = "CONTENT BODY";
					label = `"${el.textContent.trim().split(/\s+/).slice(0, 5).join(" ")}…"`;
				} else if (el.classList.contains("vp-wrapper") || el.classList.contains("vp-module")) {
					const classes = [...el.classList].filter(c => c !== "vp-wrapper" && c !== "vp-module").join(" ");
					type = `MODULE (${classes || el.tagName})`;
				} else if (el.tagName === "SECTION") {
					type = "SECTION";
				} else {
					type = el.tagName + (el.className ? `.${[...el.classList].join(".")}` : "");
				}
				console.log(`  [${idx}] ${type} ${label}  (h=${el.offsetHeight}px)`);
			});
			console.groupEnd();
		}
	}

	let parallaxBackgrounds = gsap.utils.toArray(".vp-parallax-backgrounds .background");
	let parallaxSections = document.querySelectorAll(
		".vp-parallax-section.scroll-trigger"
	);

	// mobileFactor multiplies the scroll distance — higher = slower content movement.
	const mobileFactor = window.innerWidth < 768 ? 1.75 : 1.25;

	// ─── DEBUG: SCROLL LOGGER ──────────────────────────────────────────
	if (debug) {
		let debugTick = 0;
		const viewportH = window.innerHeight;
		const contentBodies = document.querySelectorAll(".parallax-article .vp-content-body-module");

		const cbLabels = Array.from(contentBodies).map((cb, idx) => {
			return `CB${idx} "${cb.textContent.trim().split(/\s+/).slice(0, 5).join(" ")}…"`;
		});

		window.addEventListener("scroll", () => {
			debugTick++;
			if (debugTick % 30 !== 0) return;

			const scrollY = window.scrollY;
			const parts = [];

			contentBodies.forEach((cb, idx) => {
				const rect = cb.getBoundingClientRect();
				if (rect.bottom > -100 && rect.top < viewportH + 100) {
					const pos = rect.top < 0 ? "exiting-top" :
					            rect.bottom > viewportH ? "entering-bottom" : "in-view";
					parts.push(`${cbLabels[idx]} [${pos} top=${Math.round(rect.top)}]`);
				}
			});

			parallaxSections.forEach((section, idx) => {
				const rect = section.getBoundingClientRect();
				if (rect.bottom > -100 && rect.top < viewportH + 100) {
					const isPinned = Math.abs(rect.top) < 2 && Math.abs(rect.height - viewportH) < 2;
					const content = section.querySelector(".content-wrapper");
					const currentY = content ? Math.round(gsap.getProperty(content, "y")) : "?";
					const opacity = content ? gsap.getProperty(content, "opacity").toFixed(2) : "?";
					parts.push(`P${idx} [${isPinned ? "PINNED" : `top=${Math.round(rect.top)}`} y=${currentY} op=${opacity}]`);
				}
			});

			if (parts.length) {
				console.log(`scroll=${scrollY} | ${parts.join("  ·  ")}`);
			}
		}, { passive: true });
	}

	parallaxSections.forEach((section, i) => {
		const content = section.querySelector(".content-wrapper");
		const containerHeight = section.clientHeight;
		const contentHeight = content.scrollHeight;

		const baseScrollDistance =
			contentHeight > containerHeight ? contentHeight : containerHeight;
		const scrollDistance = baseScrollDistance * mobileFactor;
		const totalTravel = containerHeight + contentHeight;

		if (debug) {
			const preview = content.textContent.trim().split(/\s+/).slice(0, 5).join(" ");
			console.group(`[Parallax Section ${i}] "${preview}…"`);
			console.log(`containerHeight (100dvh section): ${containerHeight}px`);
			console.log(`contentHeight (text box):         ${contentHeight}px`);
			console.log(`baseScrollDistance (max of above): ${baseScrollDistance}px`);
			console.log(`mobileFactor:                      ${mobileFactor}`);
			console.log(`scrollDistance (base × factor):    ${scrollDistance}px`);
			console.log(`totalTravel (container + content): ${totalTravel}px`);
			console.log(`content moves ${(totalTravel / scrollDistance).toFixed(2)}px per 1px of scroll`);
			console.log(`y start: ${containerHeight}  →  y end: ${-contentHeight}`);
			console.groupEnd();
		}

		// ─── ANIMATION 1: BACKGROUND CROSSFADE ──────────────────────────
        gsap.fromTo(
            parallaxBackgrounds[i],
            {
                opacity: 0,
            },
            {
                opacity: 1,
                scrollTrigger: {
                    trigger: section,
                    start: i === 0 ? "top 95%" : "top bottom",
                    end: i === 0 ? "top 80%" : "top top",
                    scrub: true,
                },
            }
        );

		// ─── ANIMATION 2: CONTENT BOX OPACITY ───────────────────────────
		gsap.to(content, {
			opacity: 1,
			ease: "power1.inOut",
			scrollTrigger: {
				trigger: section,
				endTrigger: content,
				start: "top top",
				end: "top center",
				scrub: true,
			},
		});

		// ─── ANIMATION 3: CONTENT BOX POSITION + PIN ────────────────────
		gsap.fromTo(
			content,
			{
				y: containerHeight,
			},
			{
				y: -contentHeight,
				ease: "power1.inOut",
				scrollTrigger: {
					trigger: section,
					start: "top top",
					end: `+=${scrollDistance}`,
					scrub: true,
					pin: true,
					onToggle: debug ? (self) => {
						console.log(
							`%c[P${i} PIN ${self.isActive ? "START" : "END"}] scroll=${Math.round(window.scrollY)}`,
							self.isActive ? "color: green; font-weight: bold" : "color: red; font-weight: bold"
						);
					} : undefined,
					onUpdate: debug ? (self) => {
						const pct = Math.round(self.progress * 100);
						if (pct % 10 !== 0) return;
						const currentY = gsap.getProperty(content, "y");
						const opacity = gsap.getProperty(content, "opacity");
						const visible = currentY < containerHeight && currentY + contentHeight > 0;
						console.log(
							`[P${i}] ${pct}%  y=${Math.round(currentY)}  op=${opacity.toFixed(2)}  visible=${visible}`
						);
					} : undefined,
				},
			}
		);
	});

	if (typeof VP !== "undefined" && VP.reveal && VP.reveal.updatePosition) {
		VP.reveal.updatePosition();
	}

	let lastPageHeight = document.documentElement.scrollHeight;

	const observer = new MutationObserver((mutations) => {
		setTimeout(() => {
			const newPageHeight = document.documentElement.scrollHeight;
			if (newPageHeight !== lastPageHeight) {
				if (debug) {
					console.log(
						`%c[ScrollTrigger.refresh] page height changed: ${lastPageHeight} → ${newPageHeight}`,
						"color: orange"
					);
				}
				lastPageHeight = newPageHeight;
				ScrollTrigger.refresh();
				if (typeof VP !== "undefined" && VP.reveal && VP.reveal.updatePosition) {
					VP.reveal.updatePosition();
				}
			}
		}, 100);
	});

	observer.observe(document.body, {
		childList: true,
		subtree: true,
	});
});
